
import { useState, useEffect } from 'react';
import { ServiceModal } from './ServiceModal';

export const servicesList = [
  {
    icon: 'ri-layout-line',
    title: 'Website Design',
    description: 'Professional, conversion-optimized websites that work on any device. Get online fast with designs that actually drive results.',
    gradient: 'from-primary to-secondary',
    details: [
      'Custom responsive design tailored to your brand',
      'Mobile-first approach for all devices',
      'SEO optimization built-in from day one',
      'Fast loading speeds for better user experience',
      'Secure hosting and SSL certificate included',
      'Customized email addresses that match your website',
    ],
    benefits: [
      'Stand out from competitors with a professional online presence',
      'Convert more visitors into paying customers',
      'Build trust and credibility with your audience',
      'Save time with our done-for-you setup',
    ],
    image: '/readdy-assets/webdesign-clean-003.jpg',
  },
  {
    icon: 'ri-palette-line',
    title: 'Logo & Graphic Design',
    description: 'Memorable logos and stunning visuals that capture your brand essence. Stand out with professional designs that leave lasting impressions.',
    gradient: 'from-secondary to-primary',
    details: [
      'Custom logo design with multiple concepts',
      'Complete brand identity package available',
      'Business card and stationery design',
      'Social media graphics and templates',
      'Print-ready files in all formats (PNG, SVG, PDF)',
      'Unlimited revisions until you love it',
    ],
    benefits: [
      'Create instant brand recognition with customers',
      'Look professional from day one',
      'Consistent visuals across all platforms',
      'Own your designs forever with full rights',
    ],
    image: '/readdy-assets/logo2.jpg',
  },
  {
    icon: 'ri-mic-line',
    title: 'Voice AI Agent',
    description: 'Answer calls 24/7 with natural-sounding AI that handles inquiries, books appointments, and never misses an opportunity.',
    gradient: 'from-primary to-secondary',
    details: [
      'Natural human-like voice conversations',
      '24/7 availability - never miss a call again',
      'Intelligent call routing and transfers',
      'Appointment booking directly from calls',
      'Custom scripts tailored to your business',
      'Call recordings and transcripts for review',
    ],
    benefits: [
      'Capture leads even outside business hours',
      'Reduce missed calls by up to 90%',
      'Free up your time for high-value tasks',
      'Provide consistent customer experience every time',
    ],
    image: '/readdy-assets/voice1.jpg',
  },
  {
    icon: 'ri-chat-3-line',
    title: 'Conversational AI',
    description: 'Instant chat support that engages visitors, answers questions, and converts browsers into buyers—even while you sleep.',
    gradient: 'from-secondary to-primary',
    details: [
      'Instant responses to customer inquiries',
      'Smart FAQ handling and knowledge base',
      'Lead qualification and data capture',
      'Seamless handoff to human agents when needed',
      'Multi-language support available',
      'Integration with your existing tools',
    ],
    benefits: [
      'Engage visitors the moment they land on your site',
      'Reduce response time from hours to seconds',
      'Qualify leads automatically before you speak to them',
      'Increase conversion rates by up to 40%',
    ],
    image: '/conversational-ai-modal-correct.png',
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'Scheduler',
    description: 'Automated appointment booking that syncs with your calendar, sends reminders, and eliminates scheduling back-and-forth.',
    gradient: 'from-primary to-secondary',
    details: [
      'Real-time calendar sync with Google and Outlook',
      'Automated email and SMS reminders',
      'Custom booking pages for your services',
      'Buffer time and availability settings',
      'Team scheduling and resource management',
      'No-show protection with confirmation flows',
    ],
    benefits: [
      'Eliminate back-and-forth scheduling emails',
      'Reduce no-shows by up to 50% with reminders',
      'Let customers book anytime, anywhere',
      'Save 5+ hours per week on admin tasks',
    ],
    image: '/scheduler-modal.png',
  },
  {
    icon: 'ri-megaphone-line',
    title: 'Marketing Solutions',
    description: 'Smart campaigns that reach the right customers at the right time. Grow your business without becoming a marketing expert.',
    gradient: 'from-secondary to-primary',
    details: [
      'Social media automation and scheduling',
      'Email marketing campaigns and sequences',
      'QR code forms for offline-to-online capture',
      'Review generation and reputation management',
      'Facebook and Google Ads management (add-on)',
      'Performance tracking and analytics dashboard',
    ],
    benefits: [
      'Reach more customers without more effort',
      'Build a consistent brand presence online',
      'Turn happy customers into 5-star reviews',
      'Track ROI and see what\'s actually working',
    ],
    image: '/readdy-assets/marketing-clean-003.jpg',
  },
];

interface ServicesProps {
  openServiceTitle?: string | null;
  onModalClosed?: () => void;
}

export function Services({ openServiceTitle, onModalClosed }: ServicesProps) {
  const [selectedService, setSelectedService] = useState<typeof servicesList[0] | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Open a specific service modal when triggered from outside (e.g. footer links)
  useEffect(() => {
    if (openServiceTitle) {
      const match = servicesList.find((s) => s.title === openServiceTitle);
      if (match) {
        setSelectedService(match);
        setIsModalOpen(true);
      }
    }
  }, [openServiceTitle]);

  const handleLearnMore = (service: typeof servicesList[0]) => {
    setSelectedService(service);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedService(null);
    onModalClosed?.();
  };

  return (
    <section id="services" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-secondary mb-4">
            Solutions That Work While You Sleep
          </h2>
          <p className="text-xl text-gray-600 font-light">
            Powerful tools. One simple platform.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {servicesList.map((service, index) => (
            <div
              key={index}
              className="group bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
              onClick={() => handleLearnMore(service)}
            >
              <div className={`w-20 h-20 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-6`}>
                <i className={`${service.icon} text-4xl text-white`}></i>
              </div>

              <h3 className="font-display text-2xl font-bold text-secondary mb-4">
                {service.title}
              </h3>

              <p className="text-gray-600 leading-relaxed mb-6">
                {service.description}
              </p>

              <button
                className="text-primary font-semibold flex items-center gap-2 group-hover:gap-3 transition-all cursor-pointer whitespace-nowrap"
                onClick={(e) => {
                  e.stopPropagation();
                  handleLearnMore(service);
                }}
              >
                Learn More
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          ))}
        </div>
      </div>

      <ServiceModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        service={selectedService}
      />
    </section>
  );
}
